import Vue from 'vue';
import { Module, VuexModule, Mutation, Action } from 'vuex-module-decorators';
import { AuthStatus } from '@/utils/types';
import User, { NotificationChannel } from '@/models/user';
import axios from 'axios';
import api, { APIError, APIResponse } from '@/models/api';
import Order from '@/models/order';

export enum LoginStep {
    INIT = 'init',
    USER_EXISTS = 'user_exists',
    NEW_USER = 'new_user',
    OTP_VALIDATION = 'otp',
    EMAIL_NOT_VERIFIED = 'email_not_verified',
    LOGGED_IN = 'logged_in',
    PASSWORD_LOST = 'lost-password',
    PASSWORD_RESET = 'password-reset',
}
@Module({ name: 'user', namespaced: true })
export default class UserModule extends VuexModule {
    authStatus: AuthStatus | null = null;
    currentUser: User | null = null;
    currentUserOrders: Order[] = [];
    draftUser: User | null = null;
    isReady: boolean = false;
    loginModalOpen: boolean = false;
    loginCurrentStep: LoginStep = LoginStep.INIT;
    resetToken: string = '';
    userTempVariables: { [key: string]: string } = {
        name: '',
        phone: '',
        email: '',
        notify_by: '',
        password: '',
        default_address: '',
        business_name: '',
        membership_number: '',
        delivery_instructions: '',
    };
    otpCode: string = '';
    registrationError: string = '';
    codeGenerationError: string = '';

    @Mutation
    public setUserTempVariable(userVar: { key: string; value: string }) {
        Vue.set(this.userTempVariables, userVar.key, userVar.value);
    }

    @Mutation
    public setAuthStatus(authStatus: AuthStatus | null): void {
        this.authStatus = authStatus;
    }

    @Mutation
    public setIsReady(): void {
        this.isReady = true;
    }

    @Mutation
    public setCurrentUser(user: User | null): void {
        this.currentUser = user;
        this.draftUser = user ? new User(user) : null;
        this.isReady = true;
    }

    @Mutation
    public setUserValue(keyValue: { key: string; value: any }): void {
        if (this.draftUser) {
            Vue.set(this.draftUser, keyValue.key, keyValue.value);
        }
    }

    @Action
    public async saveDraft() {
        this.setCurrentUser(new User(this.draftUser!));
        return await this.currentUser!.update();
    }

    @Mutation
    public openLoginModal(): void {
        this.loginModalOpen = true;
        this.loginCurrentStep = LoginStep.INIT;
    }

    @Mutation
    public closeLoginModal(): void {
        this.loginModalOpen = false;
    }

    @Mutation
    public setLoginCurrentStep(step: LoginStep): void {
        this.loginCurrentStep = step;
    }

    @Mutation
    public setResetToken(token: string) {
        this.resetToken = token;
    }

    @Mutation
    public setRegistrationError(error: string) {
        this.registrationError = error;
    }

    @Mutation
    public setCodeGenerationError(error: string) {
        this.codeGenerationError = error;
    }

    @Mutation
    public setOtpCode(code: string) {
        this.otpCode = code;
    }

    @Mutation
    public setUserOrders(orders: Order[]) {
        this.currentUserOrders = orders;
    }

    @Mutation
    public updateUserOrders(orders: Order[]) {
        for (let order in orders) {
            const index = this.currentUserOrders.findIndex((ord: Order) => ord.id === orders[order].id);
            if (index !== -1) {
                this.currentUserOrders.splice(index, 1, orders[order]);
            } else {
                this.currentUserOrders.push(orders[order]);
            }
        }
    }

    @Action({ commit: 'setCurrentUser' })
    public async fetchUser(): Promise<User | null> {
        return await User.current();
    }

    @Action
    public async setupResetPassword(token: string) {
        const splitToken = token.split('_');
        const userEmail = atob(splitToken[0]);
        const resetToken = splitToken[1];
        const userExists = await User.exists(userEmail);
        if (userExists.success) {
            this.setResetToken(resetToken);
            this.setUserTempVariable({ key: 'email', value: userEmail });
            this.openLoginModal();
            this.setLoginCurrentStep(LoginStep.PASSWORD_RESET);
        }
    }

    get tempUser() {
        return new User({
            name: this.userTempVariables.name,
            email: this.userTempVariables.email,
            phone: this.userTempVariables.phone,
            provider: '',
            notify_by: this.userTempVariables.notify_by as NotificationChannel,
            default_address: this.userTempVariables.default_address,
            business_name: this.userTempVariables.business_name,
            delivery_instructions: this.userTempVariables.delivery_instructions,
            address_place_id: this.userTempVariables.address_place_id,
            fcm_token: '',
            membership_number: '',
        });
    }

    @Action
    public async requestCode(): Promise<boolean> {
        this.setCodeGenerationError('');
        const verificationSent = await this.tempUser.requestVerificationCode();
        if (verificationSent.success) {
            this.setLoginCurrentStep(LoginStep.OTP_VALIDATION);
            return true;
        }
        this.setCodeGenerationError((<APIError>verificationSent.content).message);
        return false;
    }

    @Action
    public async resendEmailVerification(): Promise<boolean> {
        const verificationSent = await this.tempUser.requestEmailVerification();
        return verificationSent.success;
    }

    @Action
    public async register(): Promise<boolean> {
        this.setRegistrationError('');
        const registerSuccess = await this.tempUser.save(this.userTempVariables.password, this.otpCode);
        if (registerSuccess.success) {
            this.setLoginCurrentStep(LoginStep.EMAIL_NOT_VERIFIED);
            return true;
        }
        if (registerSuccess.content !== null) {
            this.setRegistrationError(registerSuccess.content.message);
        }
        return false;
    }

    @Action
    public async storePushNotificationToken(token: string): Promise<boolean> {
        if (!this.currentUser || this.currentUser.fcm_token === token) {
            return false;
        }
        this.currentUser.fcm_token = token;
        return await this.currentUser.update();
    }

    @Action
    public async save(): Promise<boolean> {
        if (this.currentUser) {
            return await this.currentUser.update();
        }
        return false;
    }

    @Action({ commit: 'setCurrentUser' })
    public async logout(): Promise<null> {
        await axios.get((<any>window).route('auth.logout'));
        return null;
    }

    @Action({ commit: 'setUserOrders' })
    public async fetchUserOrders(): Promise<Order[]> {
        if (this.currentUser) {
            const userOrders = await this.currentUser.orders();
            return userOrders;
        }
        return [];
    }

    @Action({ commit: 'updateUserOrders' })
    public async fetchUserActiveOrders(): Promise<Order[]> {
        if (this.currentUser) {
            return await this.currentUser.activeOrders();
        }
        return [];
    }

    @Action
    public async downloadData(): Promise<APIResponse<{ message: string }>> {
        const response = await api.post<{ message: string }>({ name: 'gdpr.send' });
        return response;
    }

    @Action
    public async anonymizeData(): Promise<APIResponse<{ message: string }>> {
        const response = await api.post<{ message: string }>({ name: 'gdpr.anonymize' });
        return response;
    }

    @Action
    public async validateMembership(membershipNumber: string): Promise<boolean> {
        if (this.currentUser) {
            return await this.currentUser.isValidMember(membershipNumber);
        }
        return false;
    }
}
